import React, { useState, useRef, useCallback } from 'react';
import api from '../hooks/useApi';
import Icon from './Icon';
import DateInput from './DateInput';
import './ScannerRecu.css';

// ─── CONSTANTES ──────────────────────────────
const ASSOCIES      = ['LCU', 'ELA', 'ESCAMEL'];
const ASSOCIES_PERSO = ['LCU', 'ELA'];
const DESTINATIONS  = ['Compte LCU', 'Compte ELA', 'Compte Escamel', 'Cash LCU', 'Cash ELA'];
const SOUS_CATEGORIES = [
  'Achat viande / intrants (Meat/Ingredients)',
  'Équipement (Equipment)',
  'Frais administratifs (Admin)',
  'Repas/représentation (Meals & Entertainment)',
  'Vente directe (Direct Sale)',
  'Vente Woocommerce (Online Order)',
];
const MOIS_FR = [
  '', 'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre',
];
const TYPES_PERSO_LABELS = {
  bureau_electricite:          'Bureau domicile — Électricité',
  bureau_internet:             'Bureau domicile — Internet',
  bureau_loyer:                'Bureau domicile — Loyer',
  bureau_deneigement:          'Bureau domicile — Déneigement',
  bureau_assurance_habitation: 'Bureau domicile — Assurance habitation',
  bureau_taxes:                'Bureau domicile — Taxes',
  bureau_interets_hypo:        'Bureau domicile — Intérêts hypothécaires',
  interets_trailer:            'Intérêts financement trailer (100%)',
  outil_numerique:             'Outil numérique (80%)',
  deplacement:                 'Déplacement',
};

const MESSAGES_ANALYSE = [
  'Lecture du reçu...',
  'Identification des produits...',
  'Vérification de l\'inventaire...',
  'Préparation des écritures...',
];

// ─── ÉTAPES ──────────────────────────────────
const ETAPE_UPLOAD    = 'upload';
const ETAPE_ANALYSE   = 'analyse';
const ETAPE_RESULTATS = 'resultats';
const ETAPE_SUCCES    = 'succes';

export default function ScannerRecu() {
  // ── État modal ────────────────────────────
  const [ouvert, setOuvert]             = useState(false);
  const [etape, setEtape]               = useState(ETAPE_UPLOAD);

  // ── Fichier ───────────────────────────────
  const [fichier, setFichier]           = useState(null);
  const [preview, setPreview]           = useState(null);
  const [dragOver, setDragOver]         = useState(false);

  // ── Mode scan ─────────────────────────────
  const [typeDepense, setTypeDepense]   = useState('escamel'); // 'escamel' | 'perso'
  const [dejaCompta, setDejaCompta]     = useState(false);
  const [estRetour, setEstRetour]       = useState(false);     // retour / note de crédit → déduit l'inventaire
  const [associePerso, setAssociePerso] = useState('LCU');

  // ── Champs communs ────────────────────────
  const [associe, setAssocie]           = useState('LCU');
  const [destination, setDestination]   = useState('Compte LCU');
  const [fournisseur, setFournisseur]   = useState('');
  const [date, setDate]                 = useState('');
  const [notes, setNotes]               = useState('');

  // ── Analyse ───────────────────────────────
  const [msgIndex, setMsgIndex]         = useState(0);
  const [erreur, setErreur]             = useState(null);
  const [resultat, setResultat]         = useState(null);
  const [confirming, setConfirming]     = useState(false);
  const [resume, setResume]             = useState(null);

  // ── Données résultats par mode ────────────
  const [transactions, setTransactions]       = useState([]);  // escamel
  const [itemsPerso, setItemsPerso]           = useState([]);  // perso
  const [ajustementsInv, setAjustementsInv]   = useState([]);  // inventaire_seulement
  const [evaluationsActifs, setEvaluationsActifs] = useState([]); // inventaire_seulement

  // ── Prix/kg par groupe de matière (clé = matiere_trouvee.id) ─
  const [prixKgGroupes, setPrixKgGroupes] = useState({});

  const fileInputRef   = useRef(null);
  const msgIntervalRef = useRef(null);

  // Mode effectif calculé
  const modeEffectif = dejaCompta ? 'inventaire_seulement' : typeDepense;

  // ─── ROTATION MESSAGES ───────────────────
  const demarrerRotation = useCallback(() => {
    let i = 0;
    msgIntervalRef.current = setInterval(() => {
      i = (i + 1) % MESSAGES_ANALYSE.length;
      setMsgIndex(i);
    }, 1500);
  }, []);

  const arreterRotation = useCallback(() => {
    if (msgIntervalRef.current) {
      clearInterval(msgIntervalRef.current);
      msgIntervalRef.current = null;
    }
  }, []);

  // ─── RÉINITIALISER ───────────────────────
  const reinitialiser = () => {
    setEtape(ETAPE_UPLOAD);
    setFichier(null);
    setPreview(null);
    setErreur(null);
    setResultat(null);
    setTransactions([]);
    setItemsPerso([]);
    setAjustementsInv([]);
    setEvaluationsActifs([]);
    setPrixKgGroupes({});
    setFournisseur('');
    setDate('');
    setNotes('');
    setResume(null);
    setMsgIndex(0);
    setEstRetour(false);
    arreterRotation();
  };

  const fermer = () => {
    setOuvert(false);
    setTimeout(reinitialiser, 300);
  };

  // ─── GESTION FICHIER ─────────────────────
  const traiterFichier = (file) => {
    if (!file) return;
    const ext = file.name.split('.').pop().toLowerCase();
    if (!['jpg', 'jpeg', 'png', 'pdf'].includes(ext)) {
      setErreur('Format non supporté — jpg, png ou pdf seulement');
      return;
    }
    setFichier(file);
    setErreur(null);
    if (ext !== 'pdf') {
      const reader = new FileReader();
      reader.onload = (e) => setPreview(e.target.result);
      reader.readAsDataURL(file);
    } else {
      setPreview('pdf');
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    traiterFichier(e.dataTransfer.files[0]);
  };

  const onFileChange = (e) => traiterFichier(e.target.files[0]);

  // ─── ANALYSER ────────────────────────────
  const analyser = async () => {
    if (!fichier) return;
    setEtape(ETAPE_ANALYSE);
    setErreur(null);
    setMsgIndex(0);
    demarrerRotation();

    try {
      const formData = new FormData();
      formData.append('recu', fichier);
      formData.append('mode', modeEffectif);
      if (modeEffectif === 'perso') formData.append('associe_perso', associePerso);

      const data = await api.postForm('/agent/scanner-recu', formData);
      arreterRotation();
      setResultat(data);
      setFournisseur(data.fournisseur || '');
      setDate(data.date || new Date().toISOString().split('T')[0]);

      if (modeEffectif === 'escamel') {
        const txs = (data.transactions || []).map((t, i) => ({
          ...t,
          _id: i,
          _inclure: true,
          _quantite_saisie: t.quantite_ambigue ? '' : (t.quantite_kg || ''),
          taxable: t.taxable === true,
        }));
        setTransactions(txs);
      } else if (modeEffectif === 'perso') {
        const items = (data.items_perso || []).map((item, i) => ({
          ...item,
          _id: i,
          _inclure: true,
        }));
        setItemsPerso(items);
      } else {
        const ajusts = (data.ajustements_inventaire || []).map((a, i) => ({
          ...a,
          _id: i,
          _inclure: !!a.matiere_trouvee,
          _quantite_saisie: a.quantite_ambigue ? '' : (a.quantite || ''),
        }));
        setAjustementsInv(ajusts);
        const evals = (data.evaluations_actifs || []).map((e, i) => ({
          ...e,
          _id: i,
          _inclure: false,
          _annee_saisie: new Date().getFullYear(),
          _valeur_saisie: e.prix_facture || '',
          _source_saisie: e.notes || 'Prix d\'achat original',
        }));
        setEvaluationsActifs(evals);
      }

      setEtape(ETAPE_RESULTATS);
    } catch (err) {
      arreterRotation();
      const data = err.response?.data;
      const msg  = data?.error || data?.detail || err.message || 'Service d\'analyse temporairement indisponible';
      setErreur(msg);
      setEtape(ETAPE_UPLOAD);
    }
  };

  // ─── MODIFIERS ───────────────────────────
  const modifierTx = (id, champ, valeur) =>
    setTransactions(prev => prev.map(t => t._id === id ? { ...t, [champ]: valeur } : t));

  const supprimerTx = (id) =>
    setTransactions(prev => prev.filter(t => t._id !== id));

  const ajouterTx = () =>
    setTransactions(prev => [...prev, {
      _id: Date.now(), _inclure: true, _quantite_saisie: '',
      description: '', categorie: 'Dépense (Expense)',
      sous_categorie: 'Achat viande / intrants (Meat/Ingredients)',
      montant: '', inventaire: false, matiere_nom: null,
      quantite_ambigue: false, matiere_trouvee: null, stock_actuel: null,
    }]);

  const modifierItemPerso = (id, champ, valeur) =>
    setItemsPerso(prev => prev.map(i => i._id === id ? { ...i, [champ]: valeur } : i));

  const modifierAjust = (id, champ, valeur) =>
    setAjustementsInv(prev => prev.map(a => a._id === id ? { ...a, [champ]: valeur } : a));

  const modifierEval = (id, champ, valeur) =>
    setEvaluationsActifs(prev => prev.map(e => e._id === id ? { ...e, [champ]: valeur } : e));

  // ─── GROUPEMENT PAR MATIÈRE ──────────────
  function grouperTransactions(txs) {
    const ordre = [];
    const groupes = {};

    for (const t of txs) {
      const mat = t.matiere_trouvee;
      if (!mat || !t.inventaire) {
        ordre.push({ type: 'single', item: t });
        continue;
      }
      if (!groupes[mat.id]) {
        groupes[mat.id] = { type: 'groupe', matiere: mat, items: [] };
        ordre.push(groupes[mat.id]);
      }
      groupes[mat.id].items.push(t);
    }
    return ordre;
  }

  function setPrixKgGroupe(matiereId, valeur) {
    setPrixKgGroupes(prev => ({ ...prev, [matiereId]: valeur }));
  }

  function appliquerPrixKg(matiereId) {
    const prixKg = parseFloat(prixKgGroupes[matiereId]);
    if (!prixKg || prixKg <= 0) return;

    setTransactions(prev => prev.map(t => {
      if (!t.matiere_trouvee || t.matiere_trouvee.id !== matiereId) return t;
      const montant = parseFloat(t.montant) || 0;
      if (montant <= 0) return t;
      const qte = Math.round((montant / prixKg) * 1000) / 1000;
      return { ...t, _quantite_saisie: String(qte), _erreur_quantite: false };
    }));
  }

  // ─── VALIDATION ──────────────────────────
  const valider = () => {
    const actives = transactions.filter(t => t._inclure);
    const manquantes = actives.filter(t =>
      t.inventaire && t.matiere_trouvee && t.quantite_ambigue && !t._quantite_saisie
    );
    if (manquantes.length > 0) {
      setTransactions(prev => prev.map(t =>
        manquantes.find(m => m._id === t._id)
          ? { ...t, _erreur_quantite: true }
          : { ...t, _erreur_quantite: false }
      ));
      return false;
    }
    setTransactions(prev => prev.map(t => ({ ...t, _erreur_quantite: false })));
    return true;
  };

  // ─── CONFIRMER ESCAMEL ───────────────────
  const confirmerEscamel = async () => {
    if (!valider()) return;
    setConfirming(true);
    setErreur(null);

    const actives = transactions.filter(t => t._inclure);
    const transactions_journal = actives.map(t => ({
      description: t.description,
      categorie: t.categorie,
      sous_categorie: t.sous_categorie,
      montant: parseFloat(t.montant) || 0,
      notes: t.notes || null,
      taxable: !!t.taxable,
      tps_paye:   t.tps_paye   != null ? parseFloat(t.tps_paye)   : undefined,
      tvq_paye:   t.tvq_paye   != null ? parseFloat(t.tvq_paye)   : undefined,
      pourboire:  t.pourboire  != null ? parseFloat(t.pourboire)  : undefined,
      sous_total: t.sous_total != null ? parseFloat(t.sous_total) : undefined,
    }));
    const ajustements_inventaire = actives
      .filter(t => t.inventaire && t.matiere_trouvee)
      .map(t => {
        const qte   = parseFloat(t._quantite_saisie) || parseFloat(t.quantite_kg) || 0;
        const montant = parseFloat(t.montant) || 0;
        const prixU = qte > 0 ? Math.round((montant / qte) * 100) / 100 : 0;
        return { matiere_id: t.matiere_trouvee.id, quantite_ajoutee: qte, fournisseur, prix_unitaire: prixU, notes: t.description };
      })
      .filter(a => a.quantite_ajoutee > 0);

    try {
      const r = await api.post('/agent/confirmer-recu', {
        transactions_journal, ajustements_inventaire,
        recu_fichier_temp: resultat?.recu_fichier_temp || null,
        recu_type: resultat?.recu_type || null,
        associe, destination, date, fournisseur, notes: notes || null,
        est_retour: estRetour,
      });
      setResume(r);
      setEtape(ETAPE_SUCCES);
    } catch (err) {
      setErreur(err.response?.data?.error || 'Erreur lors de l\'enregistrement');
    } finally {
      setConfirming(false);
    }
  };

  // ─── CONFIRMER PERSO ─────────────────────
  const confirmerPerso = async () => {
    setConfirming(true);
    setErreur(null);

    const items_a_envoyer = itemsPerso
      .filter(i => i._inclure)
      .map(({ _id, _inclure, ...rest }) => rest);

    if (items_a_envoyer.length === 0) {
      setErreur('Aucun item sélectionné');
      setConfirming(false);
      return;
    }

    try {
      const r = await api.post('/agent/confirmer-recu-perso', {
        items_perso: items_a_envoyer,
        associe: associePerso,
        recu_fichier_temp: resultat?.recu_fichier_temp || null,
        recu_type: resultat?.recu_type || null,
        date, fournisseur, notes: notes || null,
      });
      setResume(r);
      setEtape(ETAPE_SUCCES);
    } catch (err) {
      setErreur(err.response?.data?.error || 'Erreur lors de l\'enregistrement');
    } finally {
      setConfirming(false);
    }
  };

  // ─── CONFIRMER INVENTAIRE ─────────────────
  const confirmerInventaire = async () => {
    setConfirming(true);
    setErreur(null);

    const ajusts = ajustementsInv
      .filter(a => a._inclure && a.matiere_trouvee)
      .map(a => ({
        matiere_id:       a.matiere_trouvee.id,
        quantite_ajoutee: parseFloat(a._quantite_saisie) || a.quantite || 0,
        prix_unitaire:    a.prix_unitaire || 0,
        notes:            a.matiere_nom,
      }))
      .filter(a => a.quantite_ajoutee > 0);

    const evals = evaluationsActifs
      .filter(e => e._inclure && e.actif_trouve)
      .map(e => ({
        actif_id: e.actif_trouve.id,
        annee:    parseInt(e._annee_saisie) || new Date().getFullYear(),
        valeur:   parseFloat(e._valeur_saisie) || e.prix_facture || 0,
        source:   e._source_saisie || 'Prix d\'achat original',
        notes:    e.notes || null,
      }));

    try {
      const r = await api.post('/agent/confirmer-recu-inventaire', {
        ajustements_inventaire: ajusts,
        evaluations_actifs: evals,
        recu_fichier_temp: resultat?.recu_fichier_temp || null,
        recu_type: resultat?.recu_type || null,
        date, fournisseur, notes: notes || null,
        est_retour: estRetour,
      });
      setResume(r);
      setEtape(ETAPE_SUCCES);
    } catch (err) {
      setErreur(err.response?.data?.error || 'Erreur lors de l\'enregistrement');
    } finally {
      setConfirming(false);
    }
  };

  // Dispatcher confirm selon mode
  const confirmer = () => {
    if (modeEffectif === 'escamel')    confirmerEscamel();
    else if (modeEffectif === 'perso') confirmerPerso();
    else                               confirmerInventaire();
  };

  // ─── COMPOSANTS RÉSULTATS ─────────────────

  // Bandeau de groupe
  const renderBandeauGroupe = (matiere, items) => {
    const totalMontant = items.reduce((s, t) => s + (parseFloat(t.montant) || 0), 0);
    const prixKg       = prixKgGroupes[matiere.id] || '';
    const nbItems      = items.length;
    const nbManquants  = items.filter(t =>
      t._inclure && (!t._quantite_saisie || parseFloat(t._quantite_saisie) <= 0)
    ).length;

    return (
      <div className="sr-groupe-bandeau">
        <div className="sr-groupe-header">
          <span className="sr-groupe-icon">
            <Icon name="emballage" size={20} />
          </span>
          <div className="sr-groupe-titre-wrap">
            <div className="sr-groupe-titre">{matiere.nom}</div>
            <div className="sr-groupe-meta">
              {nbItems} item{nbItems > 1 ? 's' : ''} · total {totalMontant.toFixed(2)} $
              {nbManquants > 0 && (
                <span className="sr-groupe-warn">
                  {' '}· {nbManquants} poids manquant{nbManquants > 1 ? 's' : ''}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="sr-groupe-actions">
          <div className="sr-groupe-input-wrap">
            <span className="sr-groupe-input-label">Prix / kg :</span>
            <input
              className="sr-input sr-input--qte"
              type="number" step="0.01" min="0"
              value={prixKg}
              onChange={e => setPrixKgGroupe(matiere.id, e.target.value)}
              placeholder="0.00"
            />
            <span className="sr-devise">$/kg</span>
          </div>
          <button
            className="sr-btn-appliquer"
            onClick={() => appliquerPrixKg(matiere.id)}
            disabled={!parseFloat(prixKg) || parseFloat(prixKg) <= 0}
            title={nbItems > 1 ? `Applique le calcul quantite = montant / prix_kg aux ${nbItems} items` : 'Calcule la quantite depuis le prix/kg'}
          >
            Appliquer{nbItems > 1 ? ` aux ${nbItems}` : ''}
          </button>
        </div>
      </div>
    );
  };

  // Card transaction mode escamel
  const renderCardTransaction = (t) => {
    const estAmbigue    = t.inventaire && t.matiere_trouvee && t.quantite_ambigue;
    const estEquipement = t.sous_categorie?.includes('Équipement');
    const iconName      = estEquipement ? 'parametres' : t.inventaire ? 'succes' : 'liste';
    const qteAffichee   = parseFloat(t._quantite_saisie) || parseFloat(t.quantite_kg) || 0;
    const stockApres    = t.matiere_trouvee
      ? Math.round(((t.stock_actuel || 0) + (estRetour ? -qteAffichee : qteAffichee)) * 1000) / 1000
      : null;

    return (
      <div key={t._id} className={`sr-card ${!t._inclure ? 'sr-card--exclue' : ''} ${t._erreur_quantite ? 'sr-card--erreur' : ''}`}>
        <div className="sr-card-header">
          <span className="sr-card-icon">
            <Icon name={iconName} size={16} />
          </span>
          <div className="sr-card-body">
            <input
              className="sr-input sr-input--titre"
              value={t.description}
              onChange={e => modifierTx(t._id, 'description', e.target.value)}
              placeholder="Description"
            />
            <div className="sr-card-row">
              <select
                className="sr-select sr-select--sm"
                value={t.sous_categorie || ''}
                onChange={e => modifierTx(t._id, 'sous_categorie', e.target.value)}
              >
                {SOUS_CATEGORIES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
              <div className="sr-montant-wrap">
                <input
                  className="sr-input sr-input--montant"
                  type="number" step="0.01"
                  value={t.montant}
                  onChange={e => modifierTx(t._id, 'montant', e.target.value)}
                  placeholder="0.00"
                />
                <span className="sr-devise">$</span>
              </div>
            </div>
            <label
              style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, marginTop: 6, cursor: 'pointer', userSelect: 'none' }}
              title="Cocher si l'item est taxable (TPS/TVQ inclus dans le montant)"
            >
              <input
                type="checkbox"
                checked={!!t.taxable}
                onChange={e => modifierTx(t._id, 'taxable', e.target.checked)}
              />
              <span className={t.taxable ? 'gold' : 'muted'}>
                {t.taxable ? 'Taxable (TPS/TVQ)' : 'Non taxable'}
              </span>
            </label>
          </div>
          <div className="sr-card-actions">
            <label className="sr-toggle" title={t._inclure ? 'Exclure' : 'Inclure'}>
              <input type="checkbox" checked={t._inclure} onChange={e => modifierTx(t._id, '_inclure', e.target.checked)} />
              <span className="sr-toggle-slider" />
            </label>
            <button className="sr-btn-delete" onClick={() => supprimerTx(t._id)} title="Supprimer">
              <Icon name="supprimer" size={14} />
            </button>
          </div>
        </div>

        {t.inventaire && t.matiere_trouvee && (
          <div className="sr-inventaire-bloc">
            <div className="sr-inv-label">
              <Icon name="emballage" size={14} />
              <strong>{t.matiere_trouvee.nom}</strong>
            </div>
            <div className="sr-inv-details">
              <span>Stock actuel : <strong>{t.stock_actuel} {t.matiere_trouvee.unite}</strong></span>
              <div className="sr-inv-qte">
                <span>{estAmbigue ? 'Quantité non détectée —' : (estRetour ? 'Quantité retournée :' : 'Quantité ajoutée :')}</span>
                <input
                  className={`sr-input sr-input--qte ${t._erreur_quantite ? 'sr-input--erreur' : ''}`}
                  type="number" step="0.001"
                  value={t._quantite_saisie}
                  onChange={e => modifierTx(t._id, '_quantite_saisie', e.target.value)}
                  placeholder={estAmbigue ? 'requis' : '0'}
                />
                <span>{t.matiere_trouvee.unite}</span>
              </div>
              {qteAffichee > 0 && (
                <span>Stock après : <strong className={estRetour ? 'warning' : 'success'}>{stockApres} {t.matiere_trouvee.unite}</strong></span>
              )}
              {t._erreur_quantite && <span className="danger">Quantité obligatoire</span>}
            </div>
          </div>
        )}

        {t.inventaire && !t.matiere_trouvee && t.matiere_nom && (
          <div className="sr-inventaire-bloc sr-inventaire-bloc--warning">
            <span>Matière "{t.matiere_nom}" non trouvée dans l'inventaire — stock non mis à jour</span>
          </div>
        )}

        {t.taxable && t.categorie === 'Dépense (Expense)' && t.tps_cti_potentiel > 0 && (
          <div className="sr-cti-bloc">
            <div className="sr-cti-label">
              <Icon name="idee" size={14} />
              CTI/RTI potentiel (si inscrit TPS/TVQ)
            </div>
            <div className="sr-inv-details">
              <span>TPS récupérable : <strong>{t.tps_cti_potentiel?.toFixed(2)} $</strong></span>
              <span>TVQ récupérable : <strong>{t.tvq_rti_potentiel?.toFixed(2)} $</strong></span>
              <span>Total récupérable : <strong className="success">
                {((t.tps_cti_potentiel || 0) + (t.tvq_rti_potentiel || 0)).toFixed(2)} $
              </strong></span>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Card item mode perso
  const renderCardItemPerso = (item) => (
    <div key={item._id} className={`sr-card ${!item._inclure ? 'sr-card--exclue' : ''}`}>
      <div className="sr-card-header">
        <span className="sr-card-icon">
          <Icon name="idee" size={16} />
        </span>
        <div className="sr-card-body">
          <span className="sr-input sr-input--titre" style={{ display: 'block', paddingBottom: 4 }}>
            {item.description}
          </span>
          <div className="sr-card-row">
            <span className="sr-badge-perso">
              {TYPES_PERSO_LABELS[item.type] || item.type}
            </span>
          </div>
        </div>
        <label className="sr-toggle" title={item._inclure ? 'Exclure' : 'Inclure'}>
          <input type="checkbox" checked={item._inclure}
            onChange={e => modifierItemPerso(item._id, '_inclure', e.target.checked)} />
          <span className="sr-toggle-slider" />
        </label>
      </div>
      <div className="sr-perso-bloc">
        <div className="sr-perso-row">
          <span>Montant facture</span>
          <strong>{parseFloat(item.montant)?.toFixed(2)} $</strong>
        </div>
        {item.deduction_calculee != null && (
          <div className="sr-perso-row">
            <span>Portion déductible</span>
            <strong className="success">{parseFloat(item.deduction_calculee)?.toFixed(2)} $</strong>
          </div>
        )}
        {item.pct_affaires != null && item.portion_foyer != null && (
          <div className="sr-perso-calcul">
            {(item.montant).toFixed(2)} × {(item.pct_affaires * 100).toFixed(2)}% (bureau)
            × {(item.portion_foyer * 100).toFixed(2)}%
            = {parseFloat(item.deduction_calculee)?.toFixed(2)} $
          </div>
        )}
        {item.periode_mois && (
          <div className="sr-perso-row">
            <span>Mois concerné</span>
            <span>{MOIS_FR[item.periode_mois]} {item.periode_annee}</span>
          </div>
        )}
      </div>
    </div>
  );

  // Card ajustement inventaire (mode inventaire_seulement)
  const renderCardAjustementInv = (item) => {
    const qteAffichee = parseFloat(item._quantite_saisie) || item.quantite || 0;
    const stockApres  = item.matiere_trouvee
      ? Math.round(((item.stock_actuel || 0) + (estRetour ? -qteAffichee : qteAffichee)) * 1000) / 1000
      : null;
    const estAmbigue  = item.quantite_ambigue;

    return (
      <div key={item._id} className={`sr-card ${!item._inclure ? 'sr-card--exclue' : ''}`}>
        <div className="sr-card-header">
          <span className="sr-card-icon">
            <Icon name="emballage" size={16} />
          </span>
          <div className="sr-card-body">
            <span className="sr-input sr-input--titre" style={{ display: 'block', paddingBottom: 4 }}>
              {item.matiere_nom}
            </span>
            <div className="sr-card-row">
              {item.prix_unitaire > 0 && (
                <span className="muted">{parseFloat(item.prix_unitaire).toFixed(2)} $/{item.unite}</span>
              )}
            </div>
          </div>
          <label className="sr-toggle" title={item._inclure ? 'Exclure' : 'Inclure'}>
            <input type="checkbox" checked={item._inclure}
              onChange={e => modifierAjust(item._id, '_inclure', e.target.checked)} />
            <span className="sr-toggle-slider" />
          </label>
        </div>

        {item.matiere_trouvee ? (
          <div className="sr-inventaire-bloc">
            <div className="sr-inv-label">
              <Icon name="emballage" size={14} />
              <strong>{item.matiere_trouvee.nom}</strong>
            </div>
            <div className="sr-inv-details">
              <span>Stock actuel : <strong>{item.stock_actuel} {item.matiere_trouvee.unite}</strong></span>
              <div className="sr-inv-qte">
                <span>{estAmbigue ? 'Quantité à confirmer —' : (estRetour ? 'Quantité retournée :' : 'Quantité à ajouter :')}</span>
                <input
                  className="sr-input sr-input--qte"
                  type="number" step="0.001"
                  value={item._quantite_saisie}
                  onChange={e => modifierAjust(item._id, '_quantite_saisie', e.target.value)}
                />
                <span>{item.matiere_trouvee.unite}</span>
              </div>
              {qteAffichee > 0 && (
                <span>Stock après : <strong className={estRetour ? 'warning' : 'success'}>{stockApres} {item.matiere_trouvee.unite}</strong></span>
              )}
            </div>
          </div>
        ) : (
          <div className="sr-inventaire-bloc sr-inventaire-bloc--warning">
            <span>Matière "{item.matiere_nom}" non trouvée dans l'inventaire</span>
          </div>
        )}
      </div>
    );
  };

  // Card évaluation actif (mode inventaire_seulement)
  const renderCardEvaluationActif = (item) => (
    <div key={item._id} className={`sr-card ${!item._inclure ? 'sr-card--exclue' : ''}`}>
      <div className="sr-card-header">
        <span className="sr-card-icon">
          <Icon name="parametres" size={16} />
        </span>
        <div className="sr-card-body">
          <span className="sr-input sr-input--titre" style={{ display: 'block', paddingBottom: 4 }}>
            {item.description_actif}
          </span>
          {item.actif_trouve ? (
            <span className="sr-actif-trouve">
              Trouvé : {item.actif_trouve.description} ({parseFloat(item.actif_trouve.cout_acquisition)?.toFixed(2)} $)
            </span>
          ) : (
            <span className="muted">Actif non trouvé dans le registre</span>
          )}
        </div>
        <label className="sr-toggle" title="Enregistrer l'évaluation">
          <input type="checkbox" checked={item._inclure}
            onChange={e => modifierEval(item._id, '_inclure', e.target.checked)}
            disabled={!item.actif_trouve}
          />
          <span className="sr-toggle-slider" />
        </label>
      </div>

      {item._inclure && item.actif_trouve && (
        <div className="sr-actif-bloc">
          <div className="sr-meta-row" style={{ marginTop: 8 }}>
            <label>
              Année
              <input type="number" className="sr-input"
                value={item._annee_saisie}
                onChange={e => modifierEval(item._id, '_annee_saisie', e.target.value)}
              />
            </label>
            <label>
              Valeur ($)
              <input type="number" step="0.01" className="sr-input"
                value={item._valeur_saisie}
                onChange={e => modifierEval(item._id, '_valeur_saisie', e.target.value)}
              />
            </label>
          </div>
          <label style={{ marginTop: 8 }}>
            Source
            <input className="sr-input"
              value={item._source_saisie}
              onChange={e => modifierEval(item._id, '_source_saisie', e.target.value)}
            />
          </label>
        </div>
      )}
    </div>
  );

  // ─── RENDU PRINCIPAL ─────────────────────
  return (
    <>
      <button className="sr-fab" onClick={() => setOuvert(true)} title="Scanner un reçu" aria-label="Scanner un reçu">
        <Icon name="photo" size={24} weight="bold" />
      </button>

      {ouvert && (
        <div className="sr-overlay" onClick={(e) => { if (e.target === e.currentTarget) fermer(); }}>
          <div className="sr-modal">

            {/* En-tête */}
            <div className="sr-modal-header">
              <h2 className="sr-titre">Scanner un reçu</h2>
              <button className="sr-btn-fermer" onClick={fermer} aria-label="Fermer">
                <Icon name="fermer" size={18} />
              </button>
            </div>

            {/* ── ÉTAPE 1 : UPLOAD ── */}
            {etape === ETAPE_UPLOAD && (
              <div className="sr-body">

                {/* Sélecteur de mode */}
                <div className="sr-mode-section">
                  <span className="sr-mode-label">Type de dépense</span>
                  <div className="sr-mode-toggle">
                    <button
                      className={`sr-mode-btn ${typeDepense === 'escamel' && !dejaCompta ? 'sr-mode-btn--actif' : ''}`}
                      onClick={() => { setTypeDepense('escamel'); setDejaCompta(false); }}
                    >
                      Dépense Escamel
                    </button>
                    <button
                      className={`sr-mode-btn ${typeDepense === 'perso' && !dejaCompta ? 'sr-mode-btn--actif' : ''}`}
                      onClick={() => { setTypeDepense('perso'); setDejaCompta(false); }}
                    >
                      Dépense perso
                    </button>
                  </div>

                  {typeDepense === 'perso' && !dejaCompta && (
                    <div className="sr-form-row" style={{ marginTop: 8 }}>
                      <label>
                        Associé concerné
                        <select className="sr-select" value={associePerso} onChange={e => setAssociePerso(e.target.value)}>
                          {ASSOCIES_PERSO.map(a => <option key={a} value={a}>{a}</option>)}
                        </select>
                      </label>
                    </div>
                  )}

                  <label className="sr-deja-compta">
                    <input type="checkbox" checked={dejaCompta} onChange={e => setDejaCompta(e.target.checked)} />
                    <span>Facture déjà comptabilisée (inventaire et/ou actifs seulement)</span>
                  </label>

                  {dejaCompta && (
                    <div className="sr-info-banner">
                      Aucune écriture comptable ne sera créée. Seuls l'inventaire et/ou les actifs seront mis à jour.
                    </div>
                  )}

                  {typeDepense === 'escamel' && (
                    <label className="sr-deja-compta">
                      <input type="checkbox" checked={estRetour} onChange={e => setEstRetour(e.target.checked)} />
                      <span>C'est un retour / note de crédit (déduit l'inventaire)</span>
                    </label>
                  )}

                  {estRetour && (
                    <div className="sr-info-banner">
                      Mode retour : les quantités seront <strong>déduites</strong> de l'inventaire (sortie de stock).
                      Saisis la quantité réellement retournée (un retour peut être partiel).
                    </div>
                  )}
                </div>

                {/* Zone upload */}
                <div
                  className={`sr-dropzone ${dragOver ? 'sr-dropzone--over' : ''} ${fichier ? 'sr-dropzone--ok' : ''}`}
                  onClick={() => fileInputRef.current?.click()}
                  onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={onDrop}
                >
                  <input ref={fileInputRef} type="file" accept=".jpg,.jpeg,.png,.pdf"
                    style={{ display: 'none' }} onChange={onFileChange} />
                  {fichier ? (
                    <div className="sr-preview">
                      {preview === 'pdf' ? (
                        <div className="sr-pdf-icon">
                          <Icon name="document" size={48} />
                          <span className="sr-pdf-nom">{fichier.name}</span>
                        </div>
                      ) : (
                        <img src={preview} alt="Aperçu reçu" className="sr-preview-img" />
                      )}
                      <span className="sr-preview-label">Cliquer pour changer</span>
                    </div>
                  ) : (
                    <div className="sr-dropzone-placeholder">
                      <span className="sr-dropzone-icon">
                        <Icon name="photo" size={40} />
                      </span>
                      <span>Glisser une photo ici</span>
                      <span className="muted">ou cliquer pour sélectionner</span>
                      <span className="sr-formats">JPG · PNG · PDF</span>
                    </div>
                  )}
                </div>

                {erreur && (
                  <div className="sr-erreur">
                    <Icon name="alerte" size={14} />
                    {erreur}
                  </div>
                )}

                {/* Associé + destination (mode escamel sans dejaCompta) */}
                {!dejaCompta && (
                  <div className="sr-form-row">
                    <label>
                      {typeDepense === 'escamel' ? 'Associé' : 'Payer depuis'}
                      <select value={associe} onChange={e => setAssocie(e.target.value)} className="sr-select">
                        {ASSOCIES.map(a => <option key={a} value={a}>{a}</option>)}
                      </select>
                    </label>
                    <label>
                      Compte
                      <select value={destination} onChange={e => setDestination(e.target.value)} className="sr-select">
                        {DESTINATIONS.map(d => <option key={d} value={d}>{d}</option>)}
                      </select>
                    </label>
                  </div>
                )}

                <div className="sr-footer">
                  <button className="btn btn-primary" onClick={analyser} disabled={!fichier}>
                    <Icon name="recherche" size={14} />
                    Analyser
                  </button>
                </div>
              </div>
            )}

            {/* ── ÉTAPE 2 : ANALYSE ── */}
            {etape === ETAPE_ANALYSE && (
              <div className="sr-body sr-body--center">
                <div className="sr-spinner" />
                <p className="sr-analyse-msg">{MESSAGES_ANALYSE[msgIndex]}</p>
              </div>
            )}

            {/* ── ÉTAPE 3 : RÉSULTATS ── */}
            {etape === ETAPE_RESULTATS && (
              <div className="sr-body">
                {resultat?.confiance === 'faible' && (
                  <div className="sr-warning-banner">
                    <Icon name="attention" size={14} />
                    Reçu difficile à lire — veuillez vérifier les informations ci-dessous
                  </div>
                )}
                {resultat?.notes_agent && (
                  <div className="sr-note-agent">{resultat.notes_agent}</div>
                )}

                {/* Mode inventaire_seulement : bannière */}
                {modeEffectif === 'inventaire_seulement' && (
                  <div className="sr-info-banner">
                    Aucune écriture comptable ne sera créée.
                  </div>
                )}

                {/* ── Mode Escamel ── */}
                {modeEffectif === 'escamel' && (
                  <>
                    {transactions.length === 0 && (
                      <div className="empty-state">
                        <Icon name="document" size={32} />
                        <span className="empty-state__titre">Aucune transaction détectée. Ajoutez-les manuellement.</span>
                      </div>
                    )}
                    <div className="sr-cards">
                      {grouperTransactions(transactions).map((entry, idx) => {
                        if (entry.type === 'single') {
                          return renderCardTransaction(entry.item);
                        }
                        return (
                          <div key={`groupe-${entry.matiere.id}-${idx}`} className="sr-groupe">
                            {renderBandeauGroupe(entry.matiere, entry.items)}
                            <div className="sr-groupe-items">
                              {entry.items.map(t => renderCardTransaction(t))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <button className="sr-btn-ajouter" onClick={ajouterTx}>
                      <Icon name="ajouter" size={14} />
                      Ajouter une transaction
                    </button>
                  </>
                )}

                {/* ── Mode Perso ── */}
                {modeEffectif === 'perso' && (
                  <>
                    {itemsPerso.length === 0 && (
                      <div className="empty-state">
                        <Icon name="liste" size={32} />
                        <span className="empty-state__titre">Aucun item détecté.</span>
                      </div>
                    )}
                    <div className="sr-cards">
                      {itemsPerso.map(item => renderCardItemPerso(item))}
                    </div>
                  </>
                )}

                {/* ── Mode Inventaire seulement ── */}
                {modeEffectif === 'inventaire_seulement' && (
                  <>
                    {ajustementsInv.length === 0 && evaluationsActifs.length === 0 && (
                      <div className="empty-state">
                        <Icon name="emballage" size={32} />
                        <span className="empty-state__titre">Aucun élément détecté.</span>
                      </div>
                    )}
                    {ajustementsInv.length > 0 && (
                      <>
                        <div className="sr-section-titre">Matières premières</div>
                        <div className="sr-cards">
                          {ajustementsInv.map(a => renderCardAjustementInv(a))}
                        </div>
                      </>
                    )}
                    {evaluationsActifs.length > 0 && (
                      <>
                        <div className="sr-section-titre">Actifs — évaluations</div>
                        <div className="sr-cards">
                          {evaluationsActifs.map(e => renderCardEvaluationActif(e))}
                        </div>
                      </>
                    )}
                  </>
                )}

                {/* Métadonnées */}
                <div className="sr-meta">
                  <div className="sr-meta-row">
                    <label>
                      Fournisseur
                      <input className="sr-input" value={fournisseur}
                        onChange={e => setFournisseur(e.target.value)} placeholder="Nom du fournisseur" />
                    </label>
                    <label>
                      Date
                      <DateInput className="sr-input" value={date}
                        onChange={v => setDate(v)} />
                    </label>
                  </div>

                  {modeEffectif === 'escamel' && (
                    <div className="sr-meta-row">
                      <label>
                        Associé
                        <select value={associe} onChange={e => setAssocie(e.target.value)} className="sr-select">
                          {ASSOCIES.map(a => <option key={a} value={a}>{a}</option>)}
                        </select>
                      </label>
                      <label>
                        Destination
                        <select value={destination} onChange={e => setDestination(e.target.value)} className="sr-select">
                          {DESTINATIONS.map(d => <option key={d} value={d}>{d}</option>)}
                        </select>
                      </label>
                    </div>
                  )}
                  <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, color: 'var(--muted)', fontWeight: 500 }}>
                    Notes
                    <textarea
                      className="sr-input"
                      value={notes}
                      onChange={e => setNotes(e.target.value)}
                      placeholder="Notes optionnelles..."
                      rows={2}
                      style={{ resize: 'vertical' }}
                    />
                  </label>
                </div>

                {modeEffectif === 'escamel' && (
                  <div className="sr-total">
                    Total détecté : <strong>
                      {transactions.filter(t => t._inclure)
                        .reduce((sum, t) => sum + (parseFloat(t.montant) || 0), 0)
                        .toFixed(2)} $
                    </strong>
                  </div>
                )}

                {erreur && (
                  <div className="sr-erreur">
                    <Icon name="alerte" size={14} />
                    {erreur}
                  </div>
                )}

                <div className="sr-footer sr-footer--2">
                  <button className="btn btn-ghost" onClick={reinitialiser}>Modifier</button>
                  <button
                    className="btn btn-primary"
                    onClick={confirmer}
                    disabled={confirming}
                  >
                    <Icon name="check" size={14} />
                    {confirming ? 'Enregistrement...' : 'Confirmer et enregistrer'}
                  </button>
                </div>
              </div>
            )}

            {/* ── ÉTAPE 4 : SUCCÈS ── */}
            {etape === ETAPE_SUCCES && (
              <div className="sr-body sr-body--center">
                <div className="sr-succes-icon">
                  <Icon name="succes" size={56} weight="fill" />
                </div>
                <h3 className="sr-succes-titre">Enregistré avec succès</h3>
                {resume && (
                  <div className="sr-succes-details">
                    {resume.nb_transactions > 0 && (
                      <span>{resume.nb_transactions} écriture{resume.nb_transactions > 1 ? 's' : ''} dans le journal</span>
                    )}
                    {resume.nb_ajustements > 0 && (
                      <span>{resume.nb_ajustements} ajustement{resume.nb_ajustements > 1 ? 's' : ''} d'inventaire</span>
                    )}
                    {resume.nb_bureau > 0 && (
                      <span>{resume.nb_bureau} dépense{resume.nb_bureau > 1 ? 's' : ''} bureau domicile</span>
                    )}
                    {resume.nb_autres > 0 && (
                      <span>{resume.nb_autres} autre{resume.nb_autres > 1 ? 's' : ''} dépense{resume.nb_autres > 1 ? 's' : ''} perso</span>
                    )}
                    {resume.nb_evaluations > 0 && (
                      <span>{resume.nb_evaluations} évaluation{resume.nb_evaluations > 1 ? 's' : ''} d'actif</span>
                    )}
                  </div>
                )}
                <div className="sr-footer">
                  <button className="btn btn-ghost" onClick={reinitialiser}>Nouveau scan</button>
                  <button className="btn btn-primary" onClick={fermer}>Fermer</button>
                </div>
              </div>
            )}

          </div>
        </div>
      )}
    </>
  );
}
